import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import {
  IOrderStatisticsCount,
  IContract,
  IOrderResponse,
  TOrderAction,
  CLOSED_ORDER_STATUSES,
  ORDER_STATISTICS_STATUSES,
} from '@dss-libs/portal/type/portal-types';
import { OrdersService } from '@dss-libs/portal/data-access/contract-order';
import {
  NoResultsComponent,
  OrdersSkeletonComponent,
  WidgetComponent,
} from '@dss-libs/portal/ui/portal-common-components';
import { UserManagementService } from '@dss-libs/shared/data-access/users';
import {
  UserRole,
  EventTypeEnum,
  IRecipient,
  OrderStatus,
  StepStatus,
  TConfirmationModalData,
  TIconMetaData,
  TTableAction,
  TTableActionMapper,
  TTableActionOutput,
} from '@dss-libs/shared/type/common-types';
import {
  ButtonComponent,
  ConfirmationModalComponent,
  PaginatorComponent,
  SearchComponent,
  TableComponent,
} from '@dss-libs/shared/ui/common-components';
import { AuthorizedRoleDirective } from '@dss-libs/shared/ui/common-directives';
import { ModalUiService, SnackbarService } from '@dss-libs/shared/utility/common-services';
import { isUserAuthorized } from '@dss-libs/shared/utility/common-utilities';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDatePipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { OrderStatusModalComponent } from '../order-status/order-status-modal.component';
import { SendReminderModalComponent } from '../send-reminder/send-reminder-modal.component';
import { OrderListStateService } from '../../../state/order-list-state.service';
import { getOrdersTableColumns } from '../../../constant/orders-table-columns.constant';

@Component({
  selector: 'dss-libs-feature-orders',
  imports: [
    WidgetComponent,
    SearchComponent,
    TableComponent,
    PaginatorComponent,
    OrdersSkeletonComponent,
    AuthorizedRoleDirective,
    ButtonComponent,
    TranslocoDirective,
    NoResultsComponent,
    MatIconModule,
  ],
  templateUrl: './orders.component.html',
  providers: [TranslocoDatePipe, TranslocoDecimalPipe],
})
export class OrdersComponent {
  readonly #modalService = inject(ModalUiService);
  readonly #router = inject(Router);
  readonly #snackBarService = inject(SnackbarService);
  readonly #contractOrderService = inject(OrdersService);
  readonly #userManagementService = inject(UserManagementService);
  readonly #state = inject(OrderListStateService);

  protected readonly UserRole = UserRole;

  readonly createOrderIcon: TIconMetaData = { icon: 'document-add-bold' };
  readonly currentSelectedId = signal<string | null>(null);
  readonly #status = signal(ORDER_STATISTICS_STATUSES);

  readonly pageNumber = this.#state.pageNumber;
  readonly pageSize = this.#state.pageSize;
  readonly searchValue = this.#state.searchValue;
  readonly widgetStatus = this.#state.widgetStatus;

  readonly params = computed(() => {
    const widgetStatus = this.#state.widgetStatus();
    return {
      statuses: widgetStatus === OrderStatus.Closed ? CLOSED_ORDER_STATUSES : widgetStatus ? [widgetStatus] : [],
      SearchValue: this.#state.searchValue(),
      SortValue: '',
      PageNumber: this.#state.pageNumber(),
      PageSize: this.#state.pageSize(),
      SortAscending: false,
    };
  });

  readonly ordersResource = this.#contractOrderService.ordersListingResource(this.params);
  readonly orderStatisticsResource = this.#contractOrderService.orderStatisticsResource(this.#status);

  readonly #ordersData = computed(() => (this.ordersResource.hasValue() ? this.ordersResource.value() : null));
  readonly #orderStatistics = computed(() =>
    this.orderStatisticsResource.hasValue() ? this.orderStatisticsResource.value() : null
  );

  readonly ordersListData = computed(() =>
    (this.#ordersData()?.data ?? []).map((order) => this.#mapToContractModel(order))
  );
  readonly totalCount = computed(() => this.#ordersData()?.total ?? 0);
  readonly allOrderCount = computed<IOrderStatisticsCount | null>(() => {
    const statistics = this.#orderStatistics();
    return statistics ? { statusName: OrderStatus.AllOrders, ordersCount: statistics.totalCount, id: 1 } : null;
  });
  readonly activeStatus = computed(() => this.#orderStatisticsByStatus(OrderStatus.InProgress));
  readonly completedStatus = computed(() => this.#orderStatisticsByStatus(OrderStatus.Completed));
  readonly closedStatus = computed<IOrderStatisticsCount | null>(() => {
    const statistics = this.#orderStatistics();
    if (!statistics) return null;
    const ordersCount = statistics.orderStatistics
      .filter(({ statusName }) => CLOSED_ORDER_STATUSES.includes(statusName))
      .reduce((total, status) => total + status.ordersCount, 0);

    return { statusName: OrderStatus.Closed, ordersCount };
  });

  readonly isTableLoading = this.ordersResource.isLoading;
  readonly isContentLoading = computed(() => this.orderStatisticsResource.isLoading() || this.isTableLoading());

  readonly columns = getOrdersTableColumns((orderId) => this.#orderStatusModal(orderId));

  readonly menuActions: TTableAction<TOrderAction, IContract>[] = [
    {
      action: 'details',
      label: 'ORDER-DETAILS.ORDER-OPTIONS.ORDER-DETAILS',
    },
    {
      action: 'send-reminder',
      label: 'ORDER-DETAILS.ORDER-OPTIONS.SEND-REMINDER',
      hiddenFn: ({ status }) => status !== OrderStatus.InProgress,
    },
    {
      action: 'cancel',
      label: 'ORDER-DETAILS.ORDER-OPTIONS.CANCEL-ORDER',
      hiddenFn: (order) => !this.#canCancelOrder(order),
    },
  ];

  readonly rowKey = (order: IContract) => order.id;

  readonly #actionHandlers: TTableActionMapper<IContract, TOrderAction> = {
    details: (order) => this.navigateToOrder(order.id),
    'send-reminder': (order) => this.#showSendReminderModal(order),
    cancel: (order) => this.#openCancelOrderModal(order.id),
  };

  handleAction({ action, row }: TTableActionOutput<IContract, TOrderAction>) {
    this.#actionHandlers[action](row);
  }

  #orderStatisticsByStatus(statusName: OrderStatus): IOrderStatisticsCount | null {
    const statistics = this.#orderStatistics();
    const status = statistics?.orderStatistics.find((item) => item.statusName === statusName);
    return status ? { ...status } : null;
  }

  #canCancelOrder(order: IContract) {
    const userProfile = this.#userManagementService.userProfile();
    if (order.status !== OrderStatus.InProgress || !userProfile) return false;
    if (isUserAuthorized(userProfile.role, UserRole.Admin)) return true;
    return isUserAuthorized(userProfile.role, UserRole.User) && order.userId === userProfile.id;
  }

  #refreshOrdersData() {
    this.ordersResource.reload();
    this.orderStatisticsResource.reload();
  }

  #mapToContractModel(order: IOrderResponse): IContract {
    return {
      id: order.id,
      name: order.name,
      status: order.status,
      userId: order.userId,
      orderNumber: order.orderNumber,
      lastChange: order.lastChange,
      expiryDate: order.expiryDate,
      completedCount: order.completedCount,
      recipients: order.steps?.reduce((accumulatedRecipients: IRecipient[], step) => {
        const recipients = step.recipients.map((recipient) => ({
          ...recipient,
          applicableToBeSentReminder: step.status === StepStatus.IN_PROGRESS,
        }));
        return accumulatedRecipients.concat(recipients);
      }, []),
    };
  }

  filterByWidgetName(widgetName: string) {
    if (widgetName === this.widgetStatus() && this.pageNumber() === 1) return;
    this.widgetStatus.set(widgetName);
    this.pageNumber.set(1);
    this.orderStatisticsResource.reload();
  }

  getSearchKeyWord(searchKeyWord: string) {
    this.searchValue.set(searchKeyWord);
    this.pageNumber.set(1);
  }

  #showSendReminderModal(order: IContract) {
    this.#modalService.openAbstractModal({
      component: SendReminderModalComponent,
      dialogData: { orderId: order.id, recipients: order.recipients ?? [] },
      modalDimensionsConfig: { width: '30rem' },
    });
  }

  #openCancelOrderModal(id: string) {
    this.currentSelectedId.set(id);
    const data: TConfirmationModalData = {
      svgIcon: 'danger-triangle-outline',
      iconVariant: 'danger',
      title: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.TITLE',
      subtitle: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.SUBTITLE',
      message: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.DESCRIPTION',
      callout: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.CALLOUT',
      primaryButtonLabel: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.CANCEL-ORDER',
      primaryButtonColor: 'danger',
      secondaryButtonLabel: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.KEEP-ORDER',
    };
    this.#modalService.openAbstractModal({
      component: ConfirmationModalComponent,
      dialogData: data,
      modalDimensionsConfig: { width: '30rem' },
      onSubmit: () => this.#confirmCancelOrder(),
    });
  }

  #confirmCancelOrder() {
    const id = this.currentSelectedId();
    if (!id) return;

    this.#contractOrderService.cancelOrder(id).subscribe({
      next: () => {
        this.#snackBarService.showToaster(EventTypeEnum.SUCCESS, 'ORDER-DETAILS.ORDER-CANCELLED.SUCCESS');
        this.#refreshOrdersData();
      },
      error: () => {
        this.#snackBarService.showToaster(EventTypeEnum.ERROR, 'ORDER-DETAILS.ORDER-CANCELLED.ERROR');
      },
    });
  }

  #orderStatusModal(id: string) {
    this.currentSelectedId.set(id);
    this.#modalService.openAbstractModal({
      component: OrderStatusModalComponent,
      dialogData: { orderId: id },
      modalDimensionsConfig: { width: '30rem' },
    });
  }

  navigateToOrder(id: string) {
    return this.#router.navigate(['orders', id]);
  }

  navigateToCreateOrderPage = () => void this.#router.navigate(['orders/create-order']);
}
